import { useRef, useEffect, useMemo, useState, useCallback } from 'react'
import { useRejectCandidateBulk } from '../contexts/CandidateService'
import { useRejectCandidate } from '../contexts/CandidateService/candidateServiceHooks'

interface CompanyTemplate {
    EId: string
    body: string
    channel: string
    class: string
    content: string
    default: boolean
    description: string
    flags: number
    id: number
    isDefault: boolean
    languageTag: null
    locale: string
    message: string
    messageId: "RejectionEmail"
    multipleTemplateBody: null
    multipleTemplateSubject: null
    section: null
    subject: string
    title: string
    type: "RejectionEmail"
}

export interface RejectionDisposition {
    EId: string
    name: string
}

export interface RejectionTemplateResponse {
    candidateDispositions: Array<RejectionDisposition>
    isRejectLaterEnabled: boolean
    rejectionEmailEnabled: boolean
    templates: {
        [key: string]: Array<CompanyTemplate>
    }
}

interface RejectCandidatesProps {
    applications: Array<string>
}
export default function useRejectCandidates(props: RejectCandidatesProps) {
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<RejectionTemplateResponse>()
    const [dispositions, setDispositions] = useState<Array<RejectionDisposition>>([])
    const [templates, setTemplates] = useState<Array<CompanyTemplate>>([])
    const [error, setError] = useState('')

    const rejectedCandidatesBulk = useRejectCandidateBulk()

    useEffect(() => {
        if (props.applications && props.applications.length > 0) {
            setLoading(true)
            setError('')
            rejectedCandidatesBulk({applicationEIds: props.applications}, {}, (response) => {
                setLoading(false)

                if (response.status === 200) {
                    setData(response.data)
                    setDispositions(response.data.candidateDispositions)
                    const templates: Array<CompanyTemplate> = []
                    Object.keys(response.data.templates)
                        .forEach(key => response.data.templates[key]
                        .forEach(template => templates.push(template)))
                    setTemplates(templates)
                } else {
                    setError('An unknown error occurred')
                }
            })
        }
    }, [props, rejectedCandidatesBulk])

    return useMemo(() => ({
        loading,
        error,
        dispositions,
        templates,
        isRejectLaterEnabled: (data || {}).isRejectLaterEnabled,
        rejectionEmailEnabled: (data || {}).rejectionEmailEnabled,
    }), [
        loading,
        error,
        dispositions,
        templates,
        data,
    ])
}

export interface RejectCandidatePayload {
    bulk?: boolean
    destinationJobId?: string
    appId?: string
    dispositionId: string
    sendRejectionEmail: boolean
    rejectFromAllApplications: boolean
    messageDataItem: {
        entityIdList: Array<string>
        subject: string
        content: string
        scheduleLaterNotification?: boolean
        scheduleLaterNotificationDays?: number
        locale: string
    }
    moveCandidateEnabled: boolean
}

export interface RejectCandidateResponse {
    msgInfo: number
}

export function useRejectCandidateReq() {
    let { current: mounted } = useRef(true)

    const rejectCandidates = useRejectCandidate()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [response, setResponse] = useState<RejectCandidateResponse>(null)

    const rejectCandidatePost = useCallback((data: RejectCandidatePayload) => {
        if (!mounted) return

        setLoading(true)
        setError('')
        return new Promise(function(resolve, reject) {
            try {
                rejectCandidates(data, function(response) {
                    if (!mounted) return

                    setLoading(false)
                    if (response.status === 200 && response.data.msgInfo === 200) {
                        setResponse(response.data)
                        resolve(response.data)
                    } else {
                        setError('An error occurred while trying to reject')
                        reject(response.data)
                    }
                })
            } catch(err) {
                console.log(err)
                if (!mounted) return

                reject(err)
            }
        })
    }, [rejectCandidates])

    useEffect(() => {
        mounted = true

        return () => {
            mounted = false
        }
    }, [])

    return useMemo(() => ({
        loading,
        error,
        response,
        rejectCandidatePost,
    }), [
        loading,
        error,
        response,
        rejectCandidatePost,
    ])
}
